import React, { useState, useEffect, useContext, useRef } from "react";
import { Box, Button, Grid, GridItem, Divider, Text, HStack, useToast, Spinner } from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

const COMPANY_CODES = [
  "EBL", "EMX", "ENL", "ELPL", "ENZ", "EPC", "ESA", "EPE", "EPP", "EWPL", "EAU", "EPL", "EMN", "EPDR", "EUK", "EBT", "EMCT", "EUS", "AVL", "LPK", "MNPI", "MPI", "TII", "TIL", "TIM", "ZHL"
];

const companyOptions = COMPANY_CODES.map(code => ({ label: code, value: code }));

const VendorDetailsTab = () => {
  const toast = useToast();
  const { workflowProcess } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  
  // Buyer & Company Fetching States
  const [buyerName, setBuyerName] = useState("");
  const [buyerGstin, setBuyerGstin] = useState("");
  const [isFetchingCompany, setIsFetchingCompany] = useState(false);
  const [companyCodeOptions, setCompanyCodeOptions] = useState<any[]>(companyOptions);

  const [form, setForm] = useState({
    companyCode: null as any,
    vendorSearch: "",
    selectedVendor: null as any,
    vendorCode: "",
    vendorName: "",
    vendorAddress: "",
    vendorPan: "",
    vendorBankAccount: "",
    vendorIfscCode: "",
    vendorGstIn: "",
  });

  const updateForm = (updates: any) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const lastFetchedPanVendor = useRef<string | null>(null);

  useEffect(() => {
    const fetchPanAndBankDetails = async () => {
      const vCode = form.vendorCode;
      if (!vCode || lastFetchedPanVendor.current === vCode) return;

      lastFetchedPanVendor.current = vCode;

      try {
        const response: any = await appRequest("document", "getPanBankDetails", { vendorCode: vCode });
        if (response?.status === "success" && response?.data) {
          setForm((prev: any) => ({
            ...prev,
            vendorPan: response.data.panNumber || prev.vendorPan || "",
            vendorBankAccount: response.data.bankAccountNumber || prev.vendorBankAccount || "",
            vendorIfscCode: response.data.bankIfscCode || prev.vendorIfscCode || "",
            vendorGstIn: response.data.vendorGstin || prev.vendorGstIn || "",
          }));
        }
      } catch (error) {
        console.error("Failed to load PAN and Bank Details", error);
      }
    };
    fetchPanAndBankDetails();
  }, [form.vendorCode]);

  const handleVendorSearch = async (autoQuery?: string) => {
    const query = autoQuery || form.vendorSearch;
    if (!form.companyCode || !query.trim()) {
      if (!autoQuery) {
        toast({
          title: "Missing Information",
          description: "Please select a Company Code and enter a Vendor Code/Name.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        companyCode: form.companyCode.value,
        type: "getVendorByName",
        vendorName: query.trim(),
        ...workflowProcess,
      };
      
      const res: any = await appRequest("user", "masterFinancialApi", payload);
      
      if (res?.status === "success" && Array.isArray(res?.data) && res.data.length > 0) {
        const options = res.data.map((it: any) => ({
          label: `${it?.label || it?.vendorName || it?.vendorCode} -> ${it?.vendorAddress || ""}`,
          value: it?.vendorCode,
          vendorData: it
        }));
        setVendorOptions(options);
        
        // Auto-select the first vendor option
        const firstOption = options[0];
        updateForm({
          selectedVendor: firstOption,
          vendorCode: firstOption.vendorData?.vendorCode || firstOption.value || "",
          vendorName: firstOption.vendorData?.supplierName || firstOption.vendorData?.label || firstOption.vendorData?.vendorName || "",
          vendorAddress: firstOption.vendorData?.vendorAddress || ""
        });
        
        toast({ title: "Vendors found", status: "success", duration: 2000 });
      } else {
        setVendorOptions([]);
        if (!autoQuery) {
          toast({ title: "Vendor not found", status: "error", duration: 2000 });
        }
      }
    } catch (error) {
      console.error(error);
      if (!autoQuery) {
        toast({ title: "Error fetching vendor", status: "error", duration: 2000 });
      }
    } finally {
      setLoading(false);
    }
  };

  // Automatic Fetching of Vendor Code based on Company and Buyer GSTIN/Name
  useEffect(() => {
    if (!form.companyCode?.value) return;
    if (!buyerName.trim() && !buyerGstin.trim()) return;

    const fetchGstVendorCode = async () => {
      setLoading(true);
      try {
        const payload = {
          companyCode: form.companyCode?.value,
          type: "getGstVendorCode",
          gstin: buyerGstin.trim() || buyerName.trim(),
          ...workflowProcess,
        };
        const res: any = await appRequest("user", "masterFinancialApi", payload);
        
        if (res?.status === "success" && Array.isArray(res?.data)) {
          const options = res.data.map((it: any) => ({
            label: `${it?.label || it?.vendorName || it?.vendorCode} -> ${it.vendorAddress || ""}`,
            value: it?.vendorCode,
            vendorData: it
          }));
          
          if (options.length === 0) {
            handleVendorSearch(buyerGstin.trim() || buyerName.trim());
          } else if (options.length > 0) {
            setVendorOptions(options);
            const first = options[0];
            updateForm({
              selectedVendor: first,
              vendorCode: first.vendorData?.vendorCode || first.value || "",
              vendorName: first.vendorData?.supplierName || first.vendorData?.label || first.vendorData?.vendorName || "",
              vendorAddress: first.vendorData?.vendorAddress || ""
            });
            toast({ title: "Vendors auto-fetched", status: "success", duration: 2000 });
          }
        } else {
          setVendorOptions([]);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchGstVendorCode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.companyCode, buyerName, buyerGstin, workflowProcess]);

  const handleFetchCompanyCodes = async () => {
    if (!buyerName.trim() && !buyerGstin.trim()) {
      toast({ title: "Enter Buyer Name or GSTIN", status: "warning", duration: 3000 });
      return;
    }
    setIsFetchingCompany(true);
    try {
      const payload = {
        buyerName,
        buyerGstin,
        type: "getCompanyCode",
        ...workflowProcess,
      };
      const res: any = await appRequest("user", "masterFinancialApi", payload);
      if (res?.status === "success" && Array.isArray(res?.data)) {
        const options = res.data.map((item: any) => ({ label: item, value: item }));
        setCompanyCodeOptions(options);
        if (options.length > 0) {
          updateForm({ companyCode: options[0] });
        }
        toast({ title: "Company Codes fetched successfully", status: "success", duration: 3000 });
      } else {
        setCompanyCodeOptions([]);
      }
    } catch (error: any) {
      toast({ title: "Failed to fetch Company Codes", description: error?.message, status: "error" });
    } finally {
      setIsFetchingCompany(false);
    }
  };



  const handleReset = () => {
    setBuyerName("");
    setBuyerGstin("");
    setCompanyCodeOptions(companyOptions);
    setVendorOptions([]);
    setForm({
      companyCode: null as any,
      vendorSearch: "",
      selectedVendor: null as any,
      vendorCode: "",
      vendorName: "",
      vendorAddress: "",
      vendorPan: "",
      vendorBankAccount: "",
      vendorIfscCode: "",
      vendorGstIn: "",
    });
  };

  return (
    <Box mt={2}>
      <Box display="flex" justifyContent="flex-end" mb={4}>
        <Button 
          colorScheme="red" 
          variant="outline"
          size="sm" 
          px={6} 
          onClick={handleReset} 
          shadow="sm" 
          _hover={{ bg: "red.50", transform: "translateY(-1px)", shadow: "md" }} 
          transition="all 0.2s"
          borderRadius="md"
          fontWeight="bold"
        >
          Reset Details
        </Button>
      </Box>

      {/* Buyer & Company Context Section */}
      <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" shadow="sm" mb={6} borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" color="blue.700" mb={5} borderBottom="2px solid" borderColor="blue.100" pb={2}>
          Buyer & Company Context
        </Text>
        <Grid gridTemplateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={6} alignItems="end">
          <CustomInput
            name="buyerName"
            label="Buyer Name"
            value={buyerName}
            onChange={(e: any) => setBuyerName(e.target.value)}
          />
          <CustomInput
            name="buyerGstin"
            label="Buyer GSTIN"
            value={buyerGstin}
            onChange={(e: any) => setBuyerGstin(e.target.value)}
          />
          <Button colorScheme="blue" size="md" onClick={handleFetchCompanyCodes} isLoading={isFetchingCompany} mb={1} borderRadius="md" _hover={{ bg: "blue.600" }}>
            Fetch Company Codes
          </Button>
        </Grid>
        
        <Box mt={6} w={{ base: "100%", md: "32%" }}>
          <CustomInput
            name="companyCode"
            label="Company Code"
            type="select"
            isBoxSelect
            options={companyCodeOptions}
            value={form.companyCode}
            onChange={(val: any) => updateForm({ companyCode: val })}
          />
        </Box>
      </Box>

      {/* Vendor Search Section */}
      <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" shadow="sm" mb={6} borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" color="blue.700" mb={5} borderBottom="2px solid" borderColor="blue.100" pb={2}>
          Vendor Search
        </Text>
        <Grid gridTemplateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={6} alignItems="end">
          <CustomInput
            name="vendorSearch"
            label="Search Vendor (Code or Name)"
            value={form.vendorSearch}
            onChange={(e: any) => updateForm({ vendorSearch: e.target.value })}
          />
          <Button colorScheme="blue" size="md" px={8} onClick={() => handleVendorSearch()} isLoading={loading} mb={1} borderRadius="md" _hover={{ bg: "blue.600" }}>
            Search
          </Button>
        </Grid>
      </Box>

      {/* Vendor Information Section */}
      <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" shadow="sm" borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" color="blue.700" mb={5} borderBottom="2px solid" borderColor="blue.100" pb={2}>
          Vendor Information
        </Text>
        <Grid gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <CustomInput
              name="selectedVendor"
              label="Select Vendor"
              type="select"
              isBoxSelect
              options={vendorOptions}
              value={form.selectedVendor}
              onChange={(val: any) => {
                updateForm({ 
                  selectedVendor: val,
                  vendorCode: val?.vendorData?.vendorCode || val?.value || "",
                  vendorName: val?.vendorData?.supplierName || val?.vendorData?.label || val?.vendorData?.vendorName || "",
                  vendorAddress: val?.vendorData?.vendorAddress || ""
                });
              }}
            />
          </GridItem>
          
          <CustomInput
            name="vendorCode"
            label="Vendor Code"
            value={form.vendorCode}
            onChange={(e: any) => updateForm({ vendorCode: e.target.value })}
            disabled
          />
          <CustomInput
            name="vendorName"
            label="Vendor Name"
            value={form.vendorName}
            onChange={(e: any) => updateForm({ vendorName: e.target.value })}
            disabled
          />
          
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <CustomInput
              name="vendorAddress"
              label="Vendor Address"
              value={form.vendorAddress}
              onChange={(e: any) => updateForm({ vendorAddress: e.target.value })}
              disabled
            />
          </GridItem>
          
          <CustomInput
            name="vendorPan"
            label="Vendor PAN"
            value={form.vendorPan}
            onChange={(e: any) => updateForm({ vendorPan: e.target.value })}
            disabled
          />
          <CustomInput
            name="vendorGstIn"
            label="Vendor GSTIN"
            value={form.vendorGstIn}
            onChange={(e: any) => updateForm({ vendorGstIn: e.target.value })}
            disabled
          />
          <CustomInput
            name="vendorBankAccount"
            label="Bank Account"
            value={form.vendorBankAccount}
            onChange={(e: any) => updateForm({ vendorBankAccount: e.target.value })}
            disabled
          />
          <CustomInput
            name="vendorIfscCode"
            label="IFSC Code"
            value={form.vendorIfscCode}
            onChange={(e: any) => updateForm({ vendorIfscCode: e.target.value })}
            disabled
          />
        </Grid>
      </Box>
    </Box>
  );
};

export default VendorDetailsTab;
